import crypto from 'crypto'
import {
  fetchTransactionList,
  fetchTransactionListPost,
} from '@/api/transaction'
import MerchantTransactionApi from '@/api/merchant/transactions'
import { isTransactionPostMigrationEnabled } from '@/constants/transaction'
import { normalizeBracketFilterToNested } from '@/utils/post-filter'

const SHA256 = (pattern) =>
  String(
    crypto
      .createHash('sha256')
      .update(pattern)
      .digest('hex')
  )

// XXX dirty hack because the db value for settlement status is 'settled' or empty
// But empty string, will be removed once pushed to vue-router
// We have to patching its special value here
function applySettlementStatusHack(query) {
  if (!query || typeof query !== 'object') {
    return
  }
  if (query['settlementStatus[eq]'] === 'not_settled') {
    query['settlementStatus[eq]'] = ''
  }
  if (query.settlementStatus?.eq === 'not_settled') {
    query.settlementStatus.eq = ''
  }
}

function normalizeMerchantIds(query) {
  if (!query.merchantIds) {
    return
  }

  query.merchantIds = Array.isArray(query.merchantIds)
    ? query.merchantIds
    : [query.merchantIds]
}

function prepareListRequest({ commit, state }, params) {
  const { page, limit, ...query } = params

  applySettlementStatusHack(query)

  const queryHash = SHA256(JSON.stringify(query))

  if (state.queryHash !== queryHash) {
    commit('SET_QUERY_HASH', queryHash)
    commit('REMOVE_LIST')
  }

  commit('SET_FETCHING', { name: 'fetchingList', value: true })
  normalizeMerchantIds(query)

  return { page, limit, query }
}

function commitListSuccess({ commit }, { data, page }) {
  commit('SET_FETCHING', { name: 'fetchingList', value: false })
  commit('ADD_LIST', { data, page })
  commit('SET_CURRENT_PAGE', page)
  commit('SET_TOTAL_RECORDS', data.total)
}

function commitListError({ commit }, error) {
  commit('SET_ERROR', error)
  commit('SET_FETCHING', { name: 'fetchingList', value: false })
}

function commitTotalRecords({ commit }, { approvedTotal, authorisedTotal }) {
  commit('SET_APPROVED_ORDERS_RECORDS', approvedTotal)
  commit('SET_AUTHORISED_ORDERS_RECORDS', authorisedTotal)
}

function getTotalFromResponse(response) {
  return response.data.data.length > 0 ? response.data.total : 0
}

/** Legacy GET list flow — keep intact for flag-off fallback. */
async function fetchTransactionGet(context, params) {
  try {
    const { page, limit, query } = prepareListRequest(context, params)
    const { data } = await fetchTransactionList({ page, limit, ...query })
    commitListSuccess(context, { data, page })
  } catch (error) {
    commitListError(context, error)
  }
}

/** New POST list flow (nested filter payload). */
async function fetchTransactionPost(context, params) {
  try {
    const { page, limit, query } = prepareListRequest(context, params)

    const updatedFilter = normalizeBracketFilterToNested(query)
    delete updatedFilter.merchantIds

    const { data } = await fetchTransactionListPost({
      page,
      limit,
      ...(query.merchantIds && { merchantIds: query.merchantIds }),
      ...(Object.keys(updatedFilter).length && { filter: updatedFilter }),
    })

    commitListSuccess(context, { data, page })
  } catch (error) {
    commitListError(context, error)
  }
}

/** Legacy GET totals flow — keep intact for flag-off fallback. */
async function fetchTransactionTotalRecordsGet({ commit }) {
  try {
    const approvedTransaction = await fetchTransactionList({
      'status[eq]': 'approved',
      page: 1,
      limit: 1,
    })
    const authorisedTransaction = await fetchTransactionList({
      'status[eq]': 'authorised',
      page: 1,
      limit: 1,
    })

    commitTotalRecords(
      { commit },
      {
        approvedTotal: getTotalFromResponse(approvedTransaction),
        authorisedTotal: getTotalFromResponse(authorisedTransaction),
      }
    )
  } catch (error) {
    commit('SET_ERROR', error)
  }
}

/** New POST totals flow (nested filter payload). */
async function fetchTransactionTotalRecordsPost({ commit }) {
  try {
    const approvedTransaction = await fetchTransactionListPost({
      filter: {
        status: {
          eq: 'approved',
        },
      },
      page: 1,
      limit: 1,
    })
    const authorisedTransaction = await fetchTransactionListPost({
      filter: {
        status: {
          eq: 'authorised',
        },
      },
      page: 1,
      limit: 1,
    })

    commitTotalRecords(
      { commit },
      {
        approvedTotal: getTotalFromResponse(approvedTransaction),
        authorisedTotal: getTotalFromResponse(authorisedTransaction),
      }
    )
  } catch (error) {
    commit('SET_ERROR', error)
  }
}

/** Legacy GET download flow — keep intact for flag-off fallback. */
async function downloadTransactionsGet({ commit }, data) {
  try {
    commit('SET_FETCHING', { name: 'downloadingTransaction', value: true })

    if (data['settlementStatus[eq]'] === 'not_settled') {
      // hack because the db value for settlement status is 'settled' or empty
      data['settlementStatus[eq]'] = ''
    }

    const response = await MerchantTransactionApi.downloadTransactions(data)

    commit('SET_FETCHING', { name: 'downloadingTransaction', value: false })

    return response.data
  } catch (error) {
    commit('SET_ERROR', error)
    commit('SET_FETCHING', { name: 'downloadingTransaction', value: false })

    throw error
  }
}

/** New POST download flow (nested filter payload). */
async function downloadTransactionsPost({ commit }, data = {}) {
  try {
    commit('SET_FETCHING', { name: 'downloadingTransaction', value: true })

    const {
      fromDate,
      toDate,
      timezone,
      limit,
      filter: rawFilter = {},
      ...rest
    } = data

    const filter = normalizeBracketFilterToNested({ ...rawFilter, ...rest })
    applySettlementStatusHack(filter)

    const merchantIds = filter.merchantIds
    delete filter.merchantIds

    const payload = {
      fromDate,
      toDate,
      timezone,
      limit,
      ...(merchantIds && {
        merchantIds: Array.isArray(merchantIds) ? merchantIds : [merchantIds],
      }),
      ...(Object.keys(filter).length && { filter }),
    }

    const response = await MerchantTransactionApi.downloadTransactionsPost(
      payload
    )

    commit('SET_FETCHING', { name: 'downloadingTransaction', value: false })

    return response.data
  } catch (error) {
    commit('SET_ERROR', error)
    commit('SET_FETCHING', { name: 'downloadingTransaction', value: false })

    throw error
  }
}

const state = {
  list: [],
  detail: {},
  eventHistories: {},
  transactionId: '',
  page: 1,
  limit: 25,
  queryHash: '',
  transactionTotalRecords: 0,
  approvedTransactionRecords: 0,
  authorisedTransactionRecords: 0,
  lastError: undefined,
  updatingStatus: false, // Trigger when merchant update status manually
  fetchingList: false,
  fetchingDetail: false,
  fetchingEventHistories: false,
  downloadingTransaction: false,
}

const mutations = {
  ADD_LIST(state, { data, page }) {
    state.page = Math.max(page, 1)

    state.list = data.data
  },
  REMOVE_LIST(state) {
    state.page = 1
    state.list = []
  },
  SET_FETCHING(state, { name, value }) {
    state[name] = value
  },
  SET_TOTAL_RECORDS(state, newValue) {
    state.transactionTotalRecords = newValue
  },
  SET_APPROVED_ORDERS_RECORDS(state, newValue) {
    state.approvedTransactionRecords = newValue
  },
  SET_AUTHORISED_ORDERS_RECORDS(state, newValue) {
    state.authorisedTransactionRecords = newValue
  },
  SET_CURRENT_PAGE(state, newValue) {
    state.page = newValue
  },
  SET_ERROR(state, error) {
    state.lastError = error
  },
  SET_QUERY_HASH(state, hash) {
    state.queryHash = hash
  },
}

const getters = {
  getList: (state) => {
    return state.list
  },
  getTransactionTotalRecords: (state) => {
    return state.transactionTotalRecords
  },
}

const actions = {
  async fetchTransaction(context, params) {
    if (isTransactionPostMigrationEnabled()) {
      return fetchTransactionPost(context, params)
    }

    return fetchTransactionGet(context, params)
  },
  async fetchTransactionTotalRecords(context) {
    if (isTransactionPostMigrationEnabled()) {
      return fetchTransactionTotalRecordsPost(context)
    }

    return fetchTransactionTotalRecordsGet(context)
  },
  async downloadTransactions(context, data) {
    if (isTransactionPostMigrationEnabled()) {
      return downloadTransactionsPost(context, data)
    }

    return downloadTransactionsGet(context, data)
  },
}
export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
